import { useEffect, FC, useContext } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { classes } from 'typestyle'

/* Common ======================================================================================= */
import { withRouter } from '../../../contexts/with-router'
import { GLOBAL_CONTENTS } from '../../../common/global-contents'
import { get } from '../../../share/find'
import { structureObj } from '../../../share/obj-equal'

/* Hooks ======================================================================================== */
import useLocal from '../../../hooks/use-local'
import useFetch from '../../../hooks/use-fetch'
import useBoolean from '../../../hooks/use-boolean'

/* Modules ====================================================================================== */
import '../../snippets'
import '../../../../fields'

/* Context ====================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'

/* Components =================================================================================== */
import AppLayout from '../app/layout'
import { BodyContent } from '../../../modules/layouts/body/body-content'
import { BodyHead } from '../../../modules/layouts/body/body-head'
import { Box } from '../../../components/box/box.component'
import { Form } from '../../../components/form'
import { renderSnippets } from '../../../modules/handlers/render-snippets'

/* Styles ======================================================================================= */
import { mt0sm } from '../../../styles/styleset/margin/mt0sm'

/* <AppForm /> ================================================================================== */
export const AppForm: FC<any> = ({ eject, url }) => {
  const [local] = useLocal()
  const { rowId } = useParams()
  const history = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { navigation } = useContext(NavigationContext)
  const apiUrl = url || `${process.env.REACT_APP_API_URL}${location.pathname}`

  const base = get('payload.base', navigation, 'object')
  const page = get('payload.page', navigation, 'object')
  const back = get('payload.back', navigation, 'string')

  const name = get('name', base, 'object')
  const title = get('title', page, 'object')
  const lookups = get('form.lookups', base, 'array')
  const search = { lookups }
  const snippets = get('payload.base.form.snippets', navigation, 'array')

  const [row, $row] = useFetch({}, { url: apiUrl, search })
  const [isAddingAnother, $isAddingAnother] = useBoolean(false)

  useEffect(() => {
    return () => {
      eject()
    }
  }, [])

  useEffect(() => {
    if (row.mounted && Object.keys(base).length > 0) {
      $row.get({
        url: apiUrl,
        search: { lookups },
      })
    }
  }, [base, row.mounted])

  const defaultValue: any = {}

  for (const entry of searchParams.entries()) {
    defaultValue[entry[0]] = entry[1]
  }

  let values = row.payload

  if (base?.form?.fields) {
    base?.form?.fields.map((a: any) => {
      if (a.value) {
        defaultValue[`body.${a.name}`] = a.value
      }
    })

    values = {
      ...structureObj(defaultValue),
      ...row.payload,
    }
  } else if (base?.form?.sections) {
    base?.form?.sections.map((a: any) => {
      a.fields.map((b: any) => {
        if (a.value) {
          defaultValue[`body.${a.name}.${b.name}`] = b.value
        }
      })
    })

    values = {
      ...structureObj(defaultValue),
      ...row.payload,
    }
  }

  const renderSingularName = () => {
    return ` ${'aeiou'.includes(name.singular[0].toLowerCase()) ? 'an' : 'a'} ${name.singular}`
  }

  const getNameForEdit = () => {
    if (local === 'en') {
      let toReturn = GLOBAL_CONTENTS.editFormPrefix[local]
      toReturn += renderSingularName()
      toReturn += GLOBAL_CONTENTS.editFormPostfix[local]

      return toReturn
    } else {
      let toReturn = GLOBAL_CONTENTS.editFormPrefix[local]
      toReturn += name.singular
      toReturn += GLOBAL_CONTENTS.editFormPostfix[local]

      return toReturn
    }
  }

  const getNameForAdd = () => {
    if (local === 'en') {
      let toReturn = GLOBAL_CONTENTS.addFormPrefix[local]
      toReturn += renderSingularName()
      toReturn += GLOBAL_CONTENTS.addFormPostfix[local]

      return toReturn
    } else {
      let toReturn = GLOBAL_CONTENTS.addFormPrefix[local]
      toReturn += name.singular
      toReturn += GLOBAL_CONTENTS.addFormPostfix[local]

      return toReturn
    }
  }

  const renderTitle = () => {
    if (title.form) {
      return title.form
    }
    if (location.pathname.includes('edit')) {
      if (name.singular) {
        return getNameForEdit()
      }
    }
    if (name.singular) {
      return getNameForAdd()
    }
    return '-'
  }

  return (
    <Form
      values={values as any}
      onSubmit={async (data: any) => {
        delete data._id

        if (rowId) {
          delete data.schema
          delete data.parent
          delete data.organization

          $row.post({ body: data }).then((payload: any) => {
            if (base.hidden) {
              history(-1)
            } else {
              if (isAddingAnother) {
                history(0)
              } else {
                history(`${back}/${payload.id}`)
              }
            }
          })
        } else {
          $row.post({ body: data }).then((payload: any) => {
            if (base.hidden) {
              history(-1)
            } else {
              if (isAddingAnother) {
                history(0)
              } else {
                history(`${back}/${payload.id}`)
              }
            }
          })
        }
      }}
    >
      <AppLayout.render
        headerProps={{
          maxWidth: '100%',
        }}
        toolbarProps={{
          title: renderTitle(),
          actions: [
            {
              primary: true,
              buttonProps: {
                label: GLOBAL_CONTENTS.save[local],
                type: 'submit',
              },
            },
            {
              primary: true,
              buttonProps: {
                baseline: 'background',
                type: 'submit',
                label: GLOBAL_CONTENTS.saveAndAdd[local],
                onClick: () => {
                  $isAddingAnother.set(true)
                },
              },
            },
          ],
        }}
        content={
          <AppLayout.context.Consumer>
            {() => {
              return (
                <Box baseline="background" fullWidth fullHeight className={classes(mt0sm)}>
                  <BodyHead title={renderTitle()} subtitle={name.plural} />
                  <BodyContent>{renderSnippets({ snippets })}</BodyContent>
                </Box>
              )
            }}
          </AppLayout.context.Consumer>
        }
      />
    </Form>
  )
}

export default withRouter(AppForm)
